import { type Action, createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import type { RecentlyUsedTemplate, SubTemplate } from 'src/data/template';
import { queryRecentlyUsedTemplates, querySubTemplates } from 'src/graphql/templates';
import type { ID } from 'src/lib/collection';
import type { CommonContainerProps } from 'src/lib/sweetState';

export interface State {
  subTemplates: SubTemplate[];
  loadingSubTemplates: boolean;
  recentlyUsedTemplates: RecentlyUsedTemplate[];
  loadingRecentlyUsedTemplates: boolean;
}
export type Actions = typeof ACTIONS;
export type ContainerProps = CommonContainerProps<State>;

const INITIAL_STATE: State = {
  // "sub-templates"
  subTemplates: [],
  loadingSubTemplates: false,

  // "recently used templates"
  recentlyUsedTemplates: [],
  loadingRecentlyUsedTemplates: false,
};

/**
 * Private actions
 */
// Convention not used outside the store
const PRIVATE_ACTIONS = {
  clearStore:
    (): Action<State> =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },
};

/**
 * Public actions
 */
export const ACTIONS = {
  init:
    (): Action<State> =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.clearStore());
    },

  querySubTemplates:
    (): Action<State> =>
    async ({ setState }) => {
      setState({ loadingSubTemplates: true });
      try {
        const result = await querySubTemplates();
        setState({ subTemplates: result });
      } finally {
        setState({ loadingSubTemplates: false });
      }
    },

  findSubTemplate:
    (id: ID): Action<State, null, SubTemplate | undefined> =>
    ({ getState }) => {
      return getState().subTemplates.find((subTemplate) => subTemplate.id === id);
    },

  getSubTemplates:
    (): Action<State, null, State['subTemplates']> =>
    ({ getState }) => {
      return getState().subTemplates;
    },

  queryRecentlyUsedTemplates:
    (customerId: ID, workspaceId: ID): Action<State> =>
    async ({ setState }) => {
      setState({ loadingRecentlyUsedTemplates: true });
      try {
        const result = await queryRecentlyUsedTemplates({ customerId, workspaceId });
        setState({ recentlyUsedTemplates: result });
      } finally {
        setState({ loadingRecentlyUsedTemplates: false });
      }
    },
};

export const TemplatesStore = createStore<State, Actions>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'templates',
});
